import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, ArrowUp, Minus, ShieldCheck, Trophy, UserCog, Zap } from "lucide-react";
import { useAvatar } from "../../../context/AvatarContext";
import { DASHBOARD_URL } from "../../../utils/constants";
import PlayerProfilePreview from "./PlayerProfilePreview";
import RoomCard from "./RoomCard";

function buildRows(players) {
  const sortedPlayers = [...players].sort((playerA, playerB) => {
    return (playerB.score || 0) - (playerA.score || 0);
  });

  let lastScore = null;
  let currentRank = 0;

  return sortedPlayers.map((player, index) => {
    if (player.score !== lastScore) {
      currentRank = index + 1;
      lastScore = player.score;
    }

    return { ...player, rank: currentRank };
  });
}

function MovementIcon({ movement }) {
  if (movement > 0) {
    return <ArrowUp className="h-4 w-4 text-emerald-500" />;
  }

  if (movement < 0) {
    return <ArrowDown className="h-4 w-4 text-rose-500" />;
  }

  return <Minus className="h-4 w-4 text-slate-300" />;
}


export default function RoomRanking({
  players = [],
  currentPlayerUid = null,
  canModeratePlayers = false,
  categorySlug = "",
  flawlessBonusUids = null,
}) {
  const { getPlayerAvatar } = useAvatar();
  const dashboardBaseUrl = DASHBOARD_URL;
  const previousScoresRef = useRef({});
  const initializedRef = useRef(false);
  const [scoreBursts, setScoreBursts] = useState({});
  const scoreRows = useMemo(() => buildRows(players), [players]);
  const onlineCount = useMemo(
    () => players.filter((player) => player?.online !== false).length,
    [players]
  );

  useEffect(() => {
    const previousScores = previousScoresRef.current;
    const nextBursts = {};
    const nextScores = {};

    scoreRows.forEach((player) => {
      const uid = String(player.uid);
      const nextScore = Number(player.score || 0);
      const previousScore = previousScores[uid];
      nextScores[uid] = nextScore;

      if (
        initializedRef.current &&
        previousScore !== undefined &&
        previousScore !== nextScore
      ) {
        nextBursts[uid] = {
          delta: nextScore - previousScore,
          id: `${uid}-${Date.now()}`,
        };
      }
    });

    previousScoresRef.current = nextScores;
    initializedRef.current = true;

    if (Object.keys(nextBursts).length === 0) {
      return undefined;
    }

    setScoreBursts((currentBursts) => ({
      ...currentBursts,
      ...nextBursts,
    }));

    const timeoutId = setTimeout(() => {
      setScoreBursts((currentBursts) => {
        const cleanedBursts = { ...currentBursts };
        Object.keys(nextBursts).forEach((uid) => {
          if (cleanedBursts[uid]?.id === nextBursts[uid].id) {
            delete cleanedBursts[uid];
          }
        });
        return cleanedBursts;
      });
    }, 1200);

    return () => clearTimeout(timeoutId);
  }, [scoreRows]);

  return (
    <RoomCard
      title="Classement live"
      subtitle="Scores de la partie en cours."
      className="relative bg-white"
      action={
        <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">
          <Trophy className="h-4 w-4" />
          {onlineCount} live
        </div>
      }
      contentClassName="space-y-3"
    >
      {scoreRows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
          En attente des premiers joueurs...
        </div>
      ) : (
        <div className="overflow-hidden rounded-[22px] border border-slate-200">
          <div className="grid grid-cols-[44px,minmax(0,1fr),74px] gap-2 bg-slate-50 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">
            <span>#</span>
            <span>Joueur</span>
            <span className="text-right">Score</span>
          </div>

          <div className="max-h-[420px] divide-y divide-slate-100 overflow-y-auto">
            <AnimatePresence initial={false}>
            {scoreRows.map((player) => {
              const isCurrentPlayer =
                currentPlayerUid !== null &&
                String(player.uid) === String(currentPlayerUid);
              const playerCanBeModerated = /^\d+$/.test(String(player.uid || ""));
              const scoreCounted = Boolean(player.score_counted);
              const isPodium = player.rank <= 3;
              const scoreBurst = scoreBursts[String(player.uid)];
              const burstDelta = Number(scoreBurst?.delta || 0);
              const burstPositive = burstDelta > 0;
              // delta=5 → correct+speed | delta=9 → correct+flawless | delta=10 → correct+speed+flawless
              const speedBonusBurst = burstDelta === 5 || burstDelta === 10;
              const flawlessBurst = burstDelta === 9 || burstDelta === 10;
              const teamTag = player.team?.tag || null;

              return (
                <motion.div
                  key={player.uid}
                  layout
                  layoutId={String(player.uid)}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ layout: { type: "spring", stiffness: 400, damping: 35 }, opacity: { duration: 0.2 } }}
                  className={`relative grid grid-cols-[44px,minmax(0,1fr),86px] items-center gap-2 px-3 py-3 transition duration-300 ${
                    isCurrentPlayer
                      ? "bg-cyan-950 text-white"
                      : isPodium && scoreCounted
                      ? "bg-gradient-to-r from-amber-50 via-white to-cyan-50"
                      : scoreCounted
                      ? "bg-white"
                      : "bg-slate-50/80 text-slate-500"
                  } ${
                    scoreBurst
                      ? burstPositive
                        ? "ring-2 ring-emerald-300"
                        : "ring-2 ring-rose-200"
                      : ""
                  }`}
                >
                  <AnimatePresence>
                  {scoreBurst && (
                    <motion.div
                      key={scoreBurst.id}
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.75, y: -4 }}
                      transition={{ duration: 0.18, exit: { duration: 0.22 } }}
                      className="pointer-events-none absolute right-3 top-1 inline-flex items-center gap-1"
                    >
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold shadow-sm ${burstPositive ? "bg-emerald-500 text-white" : "bg-rose-500 text-white"}`}>
                        {scoreBurst.delta > 0 ? "+" : ""}
                        {(speedBonusBurst || flawlessBurst) ? 4 : scoreBurst.delta}
                      </span>
                      {speedBonusBurst && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-400 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm">
                          <Zap className="h-3 w-3" />
                          +1
                        </span>
                      )}
                      {flawlessBurst && (
                        <span className="inline-flex items-center rounded-full bg-amber-500 px-2 py-0.5 text-[11px] font-bold text-white shadow-sm">
                          +5 SF
                        </span>
                      )}
                    </motion.div>
                  )}
                  </AnimatePresence>

                  <div className="flex items-center gap-1.5">
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-xl text-xs font-bold transition ${
                        isCurrentPlayer
                          ? "bg-white/15 text-white"
                          : isPodium && scoreCounted
                          ? "bg-amber-100 text-amber-700 shadow-[0_0_0_4px_rgba(251,191,36,0.16)]"
                          : scoreCounted
                          ? "bg-cyan-50 text-cyan-700"
                          : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      {player.rank}
                    </div>
                  </div>

                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1">
                      <PlayerProfilePreview
                        player={player}
                        avatar={getPlayerAvatar(player)}
                        isCurrentPlayer={isCurrentPlayer}
                        subtitle={null}
                        nameClassName={`truncate text-sm ${
                          isCurrentPlayer
                            ? "!text-white"
                            : player.current_correct
                            ? "text-emerald-600 font-semibold"
                            : scoreCounted
                            ? "text-slate-900"
                            : "text-slate-500"
                        }`}
                        reportCategory={categorySlug}
                      />
                      {player.is_staff ? (
                        <span title="Administrateur" className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-700">
                          <UserCog className="h-2.5 w-2.5" />
                        </span>
                      ) : player.is_moderator ? (
                        <span title="Moderateur" className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700">
                          <ShieldCheck className="h-2.5 w-2.5" />
                        </span>
                      ) : null}
                      {player.current_speed_rank === 1 && <span className="text-sm">🥇</span>}
                      {player.current_speed_rank === 2 && <span className="text-sm">🥈</span>}
                      {player.current_speed_rank === 3 && <span className="text-sm">🥉</span>}
                    </div>
                    {teamTag && (
                      <div className={`truncate text-[10px] font-semibold ${isCurrentPlayer ? "text-cyan-200" : "text-amber-700"}`}>
                        {player.team?.name ? player.team.name.slice(0, 12) : teamTag}
                      </div>
                    )}
                    {canModeratePlayers && playerCanBeModerated ? (
                      <a
                        href={`${dashboardBaseUrl.replace(/\/+$/, "")}/profiles/${player.uid}`}
                        target="_blank"
                        rel="noreferrer"
                        className={`inline-flex text-[10px] font-semibold uppercase tracking-[0.14em] ${
                          isCurrentPlayer ? "text-cyan-100" : "text-brand-600"
                        }`}
                      >
                        Modération
                      </a>
                    ) : null}
                  </div>

                  <div className="flex min-w-0 items-center justify-end gap-1.5">
                    {flawlessBonusUids && flawlessBonusUids.has(String(player.uid)) && (
                      <span className="animate-bounce rounded-full bg-amber-400 px-1.5 py-0.5 text-[10px] font-black text-white shadow">
                        +5 SF
                      </span>
                    )}
                    <MovementIcon movement={player.movement} />
                    <div className="text-right">
                      <div
                        className={`whitespace-nowrap text-base font-semibold leading-none transition ${
                          scoreBurst
                            ? burstPositive
                              ? "scale-110 text-emerald-500"
                              : "scale-110 text-rose-500"
                            : ""
                        }`}
                      >
                        {player.score ?? 0}
                      </div>
                      <div
                        className={`mt-1 text-[10px] uppercase tracking-[0.12em] ${
                          isCurrentPlayer ? "text-cyan-100" : "text-slate-400"
                        }`}
                      >
                        pts
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
            </AnimatePresence>
          </div>
        </div>
      )}
    </RoomCard>
  );
}
